import React from 'react';

const strengths = [
  {
    icon: 'I',
    title: 'Creative Writing',
    description: 'Crafting narratives that hold space for vulnerability and resilience.',
  },
  {
    icon: 'II',
    title: 'Human Connection',
    description: 'Exploring the brave defiance that lives inside every tender bond.',
  },
  {
    icon: 'III',
    title: 'Literary Depth',
    description: 'Building characters whose unspoken truths linger long after the page.',
  },
  {
    icon: 'IV',
    title: 'Atmospheric Storytelling',
    description: 'Illuminating quiet rebellions with lyrical, cinematic detail.',
  },
];

const About: React.FC = () => {
  return (
    <section id="about" className="section section--muted">
      <div className="wrapper">
        <div className="section__header">
          <span className="eyebrow">Author Statement</span>
          <h2 className="headline">What Drives Me to Write</h2>
          <p className="lead">
            I write because I feel deeply. In a world ruled by logic and material pursuits,
            writing helps me bridge the tension between heart and mind and turning emotions into
            meaning. I also write to give voice to the heart in a world that often listens only to
            the mind. Through words, I try to transform emotion into understanding and sensitivity
            into strength.
          </p>
        </div>

        <div className="grid grid--two">
          <div className="stack">
            <div className="surface">
              <p className="text-muted" style={{ fontSize: '1rem', margin: 0 }}>
                Through every story, I want to honour the quiet rebellions, the fragile
                connections, and the defiant tenderness that insist on being felt. Words allow me to
                bridge emotion and understanding—so we remember we are not alone in our ache for
                love, truth, and belonging.
              </p>
            </div>

            <div className="surface surface--subtle">
              <div className="stack">
                <div className="media-frame">
                  <img
                    src="/editorial-notes.png"
                    alt="Notes on connection — an editorial plate with an oversized quotation mark"
                    className="media media--framed"
                    style={{ maxWidth: '360px' }}
                  />
                </div>
                <p className="text-muted" style={{ margin: 0 }}>
                  I believe stories are a form of kinship. They keep us tethered to tenderness,
                  help us survive the ache of silence, and remind us that obedience is not the
                  only way to endure this world. Every chapter I write reaches for those of us
                  who loved quietly, who never stopped feeling, and who still choose courage.
                </p>
              </div>
            </div>
          </div>

          <div className="stack">
            <div className="surface surface--flat">
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', margin: '0 0 1.5rem' }}>
                How I Work with Emotion
              </h3>
              <div className="grid grid--two">
                {strengths.map((item) => (
                  <div key={item.title} className="stack" style={{ gap: '0.5rem' }}>
                    <div className="list-item__icon">{item.icon}</div>
                    <div>
                      <p style={{ margin: 0, fontWeight: 600 }}>{item.title}</p>
                      <p className="text-muted" style={{ margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="surface">
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', margin: '0 0 1rem' }}>
                Let’s Stay Connected
              </h3>
              <p className="text-muted" style={{ marginBottom: '1.5rem' }}>
                Join me on Instagram for glimpses into my drafting process, annotated lines,
                and the emotional soundtracks that keep each story alive.
              </p>
              <div className="btn-row">
                <a
                  href="https://www.instagram.com/aarjay__s/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--primary"
                >
                  Follow @aarjay__s
                </a>
                <span className="text-subtle">Daily reflections · Book releases · Writing rituals</span>
              </div>
            </div>
          </div>
        </div>

        <div className="surface surface--subtle" style={{ marginTop: '3rem' }}>
          <blockquote className="quote">
            “I write fiction reaching into the marrow of human connection—love, friendship, kinship,
            and every fragile bond that shapes us. My stories unravel delicate threads, exposing both
            tenderness and fracture, the quiet rebellions against the world&apos;s rigid orthodoxies.”
            <small>— Aarjay S</small>
          </blockquote>
        </div>
      </div>
    </section>
  );
};

export default About;

